import {Collapse, Divider, Drawer, List, useTheme, Box} from "@mui/material";
import defaultLogo from "../../../assets/alovault_logo.png";
import useDrawerItems from "./useDrawerItems";
import React, {useState} from "react";
import {useNavigate} from "react-router";
import {DrawerStyled} from "./SidebarStyles";
import Copyright from "../../shared/copyright/Copyright";
import CustomListItem from "./custom-list-tem/CustomListItem";
import drawerWidth from "../NavigationHelper";
import PropTypes from "prop-types";
import {useLogo} from "../../../contexts/logo-context/LogoContext";
import VersionDisplay from "../../shared/version/VersionDisplay";

const Sidebar = ({sidebarOpen, setSidebarOpen, setIsClosing}) => {
    const theme = useTheme();
    const [drawerItems] = useDrawerItems();
    const navigate = useNavigate();
    const [openCollapse, setOpenCollapse] = useState({});
    const { logoUrl } = useLogo();

    const handleDrawerClose = () => {
        setIsClosing(true);
        setSidebarOpen(false);
    };

    const handleDrawerTransitionEnd = () => {
        setIsClosing(false);
    };

    const handleItemClick = (route) => {
        navigate(route);
    };

    const handleOpenCollapse = (route) => {
        setOpenCollapse({[route]: !openCollapse[route]})
    }

    // The paper is a flex column: logo and footer keep their size, the nav list takes the rest
    // and scrolls on its own, so the menu can never run underneath the version / copyright block.
    const drawerPaperSx = {
        backgroundColor: theme.palette.darkBackground.main,
        color: theme.palette.common.white,
        p: 2,
        minWidth: 78,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const drawerBody = (
        <>
            {logoUrl ? (
                <Box sx={{flexShrink: 0}}>
                    <img src={logoUrl} alt="" width="100%"/>
                </Box>
            ) : (
                <Box sx={{
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 1,
                    minHeight: '64px'
                }}>
                    <img src={defaultLogo} alt="AloVault" style={{width: '70%'}}/>
                </Box>
            )}
            <List sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                mt: 1,
                py: 0,
                '&::-webkit-scrollbar': {width: '6px'},
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '3px',
                },
            }}>
                {drawerItems.map((item, idx) => (
                    item.icon ? (
                        item.subItems ?
                            <React.Fragment key={`drawer-item-${idx}`}>
                                <CustomListItem
                                    item={item}
                                    open={true}
                                    handleItemClick={() => handleOpenCollapse(idx)}
                                    openCollapse={openCollapse[idx]}
                                />
                                <Collapse in={openCollapse[idx]} timeout="auto" unmountOnExit>
                                    {
                                        item.subItems.map((subItem, id) => {
                                            return (
                                                <React.Fragment key={`drawer-subItem-${id}`}>
                                                    <CustomListItem
                                                        item={subItem}
                                                        open={true}
                                                        handleItemClick={handleItemClick}/>
                                                </React.Fragment>
                                            )
                                        })
                                    }
                                </Collapse>
                            </React.Fragment> :
                            <React.Fragment key={`drawer-item-${idx}`}>
                                <CustomListItem
                                    item={item}
                                    open={true}
                                    handleItemClick={handleItemClick}/>
                            </React.Fragment>
                    ) : <Divider key={item.text} sx={{my: 1, borderColor: "rgba(255, 255, 255, 0.2)"}}/>
                ))}
            </List>
            <Box sx={{
                flexShrink: 0,
                pt: 1,
                mt: 1,
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            }}>
                <VersionDisplay/>
                <Copyright component="div" sx={{mt: 0.5}}/>
            </Box>
        </>
    )

    return (<>
            <Drawer
                variant="temporary"
                open={sidebarOpen}
                onTransitionEnd={handleDrawerTransitionEnd}
                onClose={handleDrawerClose}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: {xs: 'block', sm: 'none'},
                    '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                }}
                PaperProps={{sx: drawerPaperSx}}
            >
                {drawerBody}
            </Drawer>
            <DrawerStyled
                variant="permanent"
                sx={{
                    display: {xs: 'none', sm: 'block'},
                    '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                }}
                PaperProps={{sx: drawerPaperSx}}
                open
            >
                {drawerBody}
            </DrawerStyled>
        </>
    );
};

Sidebar.propTypes = {
    sidebarOpen: PropTypes.bool.isRequired,
    setSidebarOpen: PropTypes.func.isRequired,
    setIsClosing: PropTypes.func.isRequired
}

export default Sidebar;
